import moment, { Moment } from "moment";
import { PrimaryTimeUtils } from "./primary-time.utils.ts";
import { Bond, Redemption, SerialBond } from "../../pages/debt-service-page.tsx";

export type DebtServiceRow = {
  date: string,
  accInterest: number,
  capitalizedInterest: number,
  debtService: number,
  annualDS?: number,
}

export type DebtServiceTable = DebtServiceRow[]

class AnnualPeriodTable {
  private periods: AnnualPeriod[];

  constructor(periods: AnnualPeriod[]) {
    this.periods = periods;
  }

  public calculate(targetAnnualDS: number, denomination: number): this {
    this.periods.reduce((prev, curr) => curr.calculatePrincipals(prev, targetAnnualDS, denomination), 0);
    return this;
  }

  public getTotalPrincipal() {
    return this.periods.reduce((prev, curr) => prev + curr.getTotalPrincipal(), 0);
  }

  public toDebtService(
    deliveryDate: Moment, 
    firstCouponDate: Moment, 
    capitalizedInterest: number | Moment,
  ): DebtServiceTable {
    const table: DebtServiceTable = this.toTable(firstCouponDate);
    this.fixLongAndShortCoupons(deliveryDate, table);
    this.applyCapitalizedInterest(deliveryDate, table, capitalizedInterest);
    this.calculateAnnualDS(table);
    return table;
  }

  private toTable(firstCouponDate: Moment): DebtServiceTable {
    const table: DebtServiceTable = [];
    let accInterest: number = 0;

    for (const annualPeriod of this.periods) {
      for (const repaymentPeriod of annualPeriod.getRepaymentPeriods()) {
        const date = moment(repaymentPeriod.date);
        if (date.isBefore(firstCouponDate, "day")) break;
        const dateIso = date.toISOString();
        accInterest += repaymentPeriod.calculateAddedInterest();
        const debtService = accInterest + repaymentPeriod.getTotalRepaymentPrincipal();
        table.push({ date: dateIso, accInterest, capitalizedInterest: 0, debtService });
      }
    }

    return table.reverse();
  }

  private applyCapitalizedInterest(deliveryDate: Moment, table: DebtServiceTable, capitalizedInterest: number | Moment) {
    const isAmount = typeof capitalizedInterest === "number";
    const capitalizedUntil = isAmount ? moment("3000-01-01", "YYYY-MM-DD") : capitalizedInterest;
    let moneyLeft = isAmount ? capitalizedInterest : Number.MAX_VALUE;

    for (let index = 0; index < table.length; index++) {
      const debtRow = table[index];
      const debtPeriodEnd = moment(debtRow.date).clone();
      const debtPeriodStart = index === 0 ? deliveryDate.clone() : debtPeriodEnd.clone().subtract(6, "months");

      if (moneyLeft === 0 || capitalizedUntil.isBefore(debtPeriodStart, "day")) {
        break;
      }

      // The whole coupon payment is covered.
      else if (capitalizedUntil.isSameOrAfter(debtPeriodEnd)) {
        debtRow.capitalizedInterest = Math.min(moneyLeft, debtRow.accInterest);
        debtRow.debtService -= debtRow.capitalizedInterest;
        moneyLeft -= debtRow.capitalizedInterest;
      }

      // The coupon payment is partially covered
      else {
        const periodDuration = PrimaryTimeUtils.daysDifference360(debtPeriodStart, debtPeriodEnd);
        const capitalizedDays = PrimaryTimeUtils.daysDifference360(debtPeriodStart, capitalizedUntil);
        const capitalizedFactor = capitalizedDays / periodDuration;
        debtRow.capitalizedInterest = Math.min(moneyLeft, debtRow.accInterest * capitalizedFactor);
        debtRow.debtService -= debtRow.capitalizedInterest;
        moneyLeft -= debtRow.capitalizedInterest;
        break;
      }
    }
  }

  private fixLongAndShortCoupons(deliveryDate: Moment, table: DebtServiceTable) {
    if (table.length === 0) return;
    const firstCouponPayment = table[0];

    const daysToFirstCoupon = PrimaryTimeUtils.daysDifference360(deliveryDate, moment(firstCouponPayment.date));
    const longCouponInterest = firstCouponPayment.accInterest * daysToFirstCoupon / 180;
    const couponDifference = longCouponInterest - firstCouponPayment.accInterest;
    firstCouponPayment.accInterest += couponDifference;
    firstCouponPayment.debtService += couponDifference;
  }

  private calculateAnnualDS(table: DebtServiceTable) {
    for (let i = table.length - 1; i >= 0; i -= 2) {
      let totalDebt = table[i].debtService;
      if (i !== 0) totalDebt += table[i - 1].debtService;
      table[i].annualDS = totalDebt;
    }
  }

  public print() {
    const table: any[] = [];
    for (const annualPeriod of this.periods) {
      for (const repaymentPeriod of [annualPeriod.firstPeriod, annualPeriod.secondPeriod]) {
        for (const adapter of repaymentPeriod.adapters) {
          table.push({
            "date": adapter.getDate(),
            "principal": adapter.getPrincipal(),
            "coupon": adapter.getCoupon(),
          })
        }
      }
    }
    console.table(table);
  }

}

// Represents a 1 year interval
class AnnualPeriod {
  public firstPeriod: RepaymentPeriod;
  public secondPeriod: RepaymentPeriod;

  constructor(firstPeriod: RepaymentPeriod, secondPeriod: RepaymentPeriod) {
    this.firstPeriod = firstPeriod;
    this.secondPeriod = secondPeriod;
  }

  // Calculates and updates the bond principals to obtain the desired annual DS.
  // Returns the new accumulated interest.
  public calculatePrincipals(prevInterest: number, targetAnnualDS: number, denomination: number): number {
    const modifyFirst = this.firstPeriod.canModifyPrincipal();
    const modifySecond = this.secondPeriod.canModifyPrincipal();

    // Option 1: We can't modify any principal, as such we can skip the calculation.
    if (!modifyFirst && !modifySecond) {
      return prevInterest + this.getTotalInterestIncrease();
    }

    const p1WeightedCoupon = this.firstPeriod.getWeightedCoupon();
    const p1AccrueCoupon = p1WeightedCoupon / 2;
    const p2WeightedCoupon = this.secondPeriod.getWeightedCoupon();
    const p2AccrueCoupon = p2WeightedCoupon / 2;

    // Option 2: Only the first payment can be modified
    if (modifyFirst && !modifySecond) {
      const payment2 = this.secondPeriod.getTotalPrincipal();
      const principal = (targetAnnualDS - payment2 * (1 + p2AccrueCoupon) - 2 * prevInterest) / (1 + 2 * p1AccrueCoupon);
      this.firstPeriod.setTotalPrincipal(principal, p1WeightedCoupon, denomination);
    }

    // Option 3: Only the second payment can be modified
    else if (!modifyFirst && modifySecond) {
      const payment1 = this.firstPeriod.getTotalPrincipal();
      const principal = (targetAnnualDS - payment1 * (1 + 2 * p1AccrueCoupon) - 2 * prevInterest) / (1 + p2AccrueCoupon);
      this.secondPeriod.setTotalPrincipal(principal, p2WeightedCoupon, denomination);
    }

    // Option 4: Both payments are unknown.
    // We create a smooth descencing principal setting P2 = (1 + AC(1)) * P1
    else {
      const p1Principal = (targetAnnualDS - 2 * prevInterest) / ((1 + 2 * p1AccrueCoupon) + (1 + p1AccrueCoupon) * (1 + p2AccrueCoupon));
      const p2Principal = p1Principal * (1 + p1AccrueCoupon)
      this.firstPeriod.setTotalPrincipal(p1Principal, p1WeightedCoupon, denomination);
      this.secondPeriod.setTotalPrincipal(p2Principal, p2WeightedCoupon, denomination);
    }

    return prevInterest + this.getTotalInterestIncrease();
  }

  public getRepaymentPeriods() {
    return [this.firstPeriod, this.secondPeriod];
  }

  public getTotalPrincipal() {
    return this.getRepaymentPeriods().reduce((prev, curr) => prev + curr.getTotalPrincipal(), 0);
  }

  public getTotalInterestIncrease() {
    return this.getRepaymentPeriods().reduce((prev, curr) => prev + curr.calculateAddedInterest(), 0);
  }
}

// Represents a 6 month repayment interval
class RepaymentPeriod {
  public adapters: BondAdapter[];
  public date: string;

  constructor(adapters: BondAdapter[], date: string) {
    this.adapters = adapters;
    this.date = date;
  }

  public getTotalPrincipal() {
    return this.adapters.reduce((prev, curr) => prev + curr.getPrincipal(), 0);
  }

  public getTotalRepaymentPrincipal() {
    return this.adapters.reduce((prev, curr) => prev + curr.getPrincipal() * (curr.getRedemptionPrice() / 100), 0);
  }

  public canModifyPrincipal() {
    return this.adapters.some(x => !x.isLocked());
  }

  public getDates() {
    return this.adapters.map(x => x.getDate());
  }

  public calculateAddedInterest() {
    return this.adapters.reduce((prev, curr) => prev + curr.getPrincipal() * curr.getCoupon() / 2, 0);
  }

  public getWeightedCoupon() {
    if (this.adapters.length === 0) return 0.05;
    
    const totalPrincipal = this.getTotalPrincipal();
    if (totalPrincipal === 0) {
      return this.adapters.reduce((prev, curr) => prev + curr.getCoupon(), 0) / this.adapters.length;
    } else {
      return this.adapters.reduce((prev, curr) => prev + curr.getPrincipal() * curr.getCoupon(), 0) / totalPrincipal;
    }
  }

  public setTotalPrincipal(principal: number, weightedCoupon: number, denomination: number) {
    let principalLeft = principal * weightedCoupon;

    const lockedAdapters = this.adapters.filter(x => x.isLocked());
    const unlockedAdapters = this.adapters.filter(x => !x.isLocked());

    lockedAdapters.forEach(adapter => {
      principalLeft -= adapter.getPrincipal() * adapter.getCoupon();
    });

    const totalUnlockedCoupon = unlockedAdapters.reduce((prev, curr) => prev + curr.getCoupon(), 0);
    unlockedAdapters.forEach(adapter => {
      const adpaterPrincipal = principal * (adapter.getCoupon() / totalUnlockedCoupon);
      const adapterRoundedPrincipal = Math.round(adpaterPrincipal / denomination) * denomination;
      adapter.setPrincipal(adapterRoundedPrincipal);
      principalLeft -= adapter.getPrincipal() * adapter.getCoupon();
    });
  }
}

class BondAdapter {
  private bond: SerialBond| Redemption;
  private date: string;
  private coupon: number;
  private redemptionPrice: number;

  constructor(bond: SerialBond | [Redemption, number]) {
    if (Array.isArray(bond)) {
      this.bond =  bond[0];
      this.date = bond[0].date;
      this.coupon = bond[1];
      this.redemptionPrice = bond[0].redemptionPrice;
    } else {
      this.bond = bond;
      this.date = bond.maturity;
      this.coupon = bond.coupon;
      this.redemptionPrice = 100;
    }    
  }

  public isSerialBond(value: SerialBond | Redemption): value is SerialBond {
    return "principal" in value;
  }

  public getDate() {
    return this.date;
  }

  public getRedemptionPrice() {
    return this.redemptionPrice;
  }

  public getPrincipal() {
    return this.isSerialBond(this.bond) ? this.bond.principal : this.bond.amount;
  }

  public setPrincipal(amount: number) {
    if (this.isSerialBond(this.bond)) {
      this.bond.principal = amount;
    } else {
      this.bond.amount = amount;
    }
  }

  public getCoupon() {
    return this.coupon;
  }

  public isLocked(): boolean {
    return this.isSerialBond(this.bond) ? this.bond.lockedPrincipal : this.bond.locked;
  }

  public setLocked(locked: boolean) {
    if (this.isSerialBond(this.bond)) {
      this.bond.lockedPrincipal = locked;
    } else {
      this.bond.locked = locked;
    }
  }
}

export class DebtServiceUtils {


  datesToDefaultBonds
  
  /**
   * Calculates the debt service of a list of bonds and modifies the bond principals to
   * reach the target total par amount.
   */
  public static calculateDebtService(
    recalculatePrincipals: boolean,
    deliveryDate: Moment, 
    firstCouponDate: Moment, 
    targetParAmount: number, 
    denomination: number,
    bonds: Bond[],
    capitalizedInterest: number | Moment,
  ) {
    if (bonds.length === 0) return [];

    const annualPeriodTable = this.bondsToAnnualPeriodTable(firstCouponDate, bonds);
    if (recalculatePrincipals) {
      const dsHelper = (annualDebt: number) => annualPeriodTable.calculate(annualDebt, denomination).getTotalPrincipal();
      const maxIterations = recalculatePrincipals ? 100 : 1;
      this.linearBinarySearch(targetParAmount, -targetParAmount, targetParAmount, maxIterations, dsHelper, 1);
    }

    return annualPeriodTable.toDebtService(
      deliveryDate, 
      firstCouponDate, 
      capitalizedInterest,
    );
  }

  private static bondsToAnnualPeriodTable(
    firstCouponDate: Moment,
    bonds: Bond[]
  ): AnnualPeriodTable {
    // 1. Transform bonds into a map of date to adapters
    let lastPrincipalDate = firstCouponDate.clone();
    const dateAdapterMap: Map<string, BondAdapter[]> = new Map();
    const DATE_FMT = "YYYY-MM-DD";
    
    for (const bond of bonds) {
      if (bond.type === "serial") {
        const date = moment(bond.maturity).format(DATE_FMT);
        const current = dateAdapterMap.get(date) || [];
        current.push(new BondAdapter(bond));
        dateAdapterMap.set(date, current);
        lastPrincipalDate = moment.max(lastPrincipalDate, moment(bond.maturity));
      } else {
        for (let i = 0; i < bond.redemptions.length; i++) {
          const redemption = bond.redemptions[i];
          const date = moment(redemption.date).format(DATE_FMT);
          const current = dateAdapterMap.get(date) || [];
          current.push(new BondAdapter([redemption, bond.coupon]));
          dateAdapterMap.set(date, current);
          lastPrincipalDate = moment.max(lastPrincipalDate, moment(redemption.date));
        }
      }
    }

    // 2. Transform the list of adapters to annual periods
    const dateToRepaymentPeriodMap: Map<string, RepaymentPeriod> = new Map();
    for (const [date, adapters] of dateAdapterMap) {
      dateToRepaymentPeriodMap.set(date, new RepaymentPeriod(adapters, date));
    }

    const result: AnnualPeriod[] = [];
    const currentDate = lastPrincipalDate.clone();
    while (currentDate.isSameOrAfter(firstCouponDate)) {
      const firstPeriodDate = currentDate.format(DATE_FMT);
      const firstPeriodAdapters = dateAdapterMap.get(firstPeriodDate) || [];
      const firstPeriodRepayment = new RepaymentPeriod(firstPeriodAdapters, firstPeriodDate);
      currentDate.subtract(6, "months");
      const secondPeriodDate = currentDate.format(DATE_FMT);
      const secondPeriodAdapters = dateAdapterMap.get(secondPeriodDate) || [];
      const secondPeriodRepayment = new RepaymentPeriod(secondPeriodAdapters, secondPeriodDate);
      currentDate.subtract(6, "months");
      result.push(new AnnualPeriod(firstPeriodRepayment, secondPeriodRepayment))
    }

    return new AnnualPeriodTable(result);
  }

  /**
   * Returns a list of default bonds.
   */
  public static datesToDefaultBonds(
    firstPrincipalDate: Moment,
    lastPrincipalDate: Moment,
    principalFrequency: number,
  ) {
    const months = 12 / principalFrequency;

    const bonds: Bond[] = [];
    const current = firstPrincipalDate.clone();
    while (current.isSameOrBefore(lastPrincipalDate, "day")) {
      bonds.push({
        type: "serial",
        coupon: 0.05,
        lockedCoupon: false,
        lockedPrincipal: false,
        maturity: current.toISOString(),
        principal: 0
      })

      current.add(months, "months");
    }

    return bonds;
  }

  /**
   * Performs a linear binary search to find an input value that makes a given function reach a target output.
   * @param {number} target - Desired output of the function.
   * @param {number} min - Minimum search boundary.
   * @param {number} max - Maximum search boundary.
   * @param {number} maxIterations - Maximum iterations allowed.
   * @param {(val: number) => number} func - Function used for evaluation.
   * @param {number} [accurracy=10e-5] - Acceptable error margin.
   * @returns {number} Input value producing an output close to the target.
   */
  private static linearBinarySearch(
    target: number,
    min: number, 
    max: number,
    maxIterations: number,
    func: (val: number) => number,
    accurracy: number = 10e-5
  ) {
    let mid = 0;

    for (let i = 0; i <= maxIterations; i++) {
      mid = min + (max - min) / 2;
      const result = func(mid);

      const error = Math.abs(result - target);
      if (error <= accurracy) return mid;
      
      if (result < target) {
        min = mid;
      } else {
        max = mid;
      }
    }

    return mid;
  }
}
